import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Lightbulb, Rocket, Users, Brain, Heart, Shield, Target, Zap, TrendingUp, Award, Building2, Network, ArrowRight, Crown, CheckCircle, Workflow, Clock } from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface PRIsMEManifesteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PRIsMEManifesteModal({ isOpen, onClose }: PRIsMEManifesteModalProps) {
  const [activeTab, setActiveTab] = useState(0);
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['prisme_manifeste_last_ai_analysis'] ?? null) : null;

  if (!isOpen) return null;

  const tabs = [
    { id: 0, name: '💎 Les 5 BIG IDEAS', icon: <Lightbulb className="w-5 h-5" /> },
    { id: 1, name: '🌱 L\'Origine', icon: <Rocket className="w-5 h-5" /> },
    { id: 2, name: '🔥 Le Manifeste', icon: <Heart className="w-5 h-5" /> },
    { id: 3, name: '🎨 Pourquoi PRIsME?', icon: <Brain className="w-5 h-5" /> },
    { id: 4, name: '🏛️ Architecture & Gouvernance', icon: <Building2 className="w-5 h-5" /> },
    { id: 5, name: '🚀 Devenez Ambassadeurs', icon: <Award className="w-5 h-5" /> },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div id="prisme-manifeste-content" className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl w-[80%] max-w-[90%] my-8 border border-indigo-500/30 shadow-2xl shadow-indigo-500/20">
        {/* Header */}
        <div className="relative p-8 border-b border-indigo-500/30">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl">
                <Brain className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
                    PRIsME
                  </h2>
                  {/* Badge AI - Disponible dans AI Assistant */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                    className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                    title="Disponible dans AI Assistant"
                  >
                    <Brain className="w-4 h-4 text-purple-200" />
                    <span className="text-xs text-purple-100 font-semibold">AI</span>
                  </motion.div>
                </div>
                <p className="text-gray-400 mt-1 text-lg">
                  Plateforme interne BZ Inc. pour l'excellence organisationnelle et l'excellence TI
                </p>
                <p className="text-indigo-300 text-sm mt-1">
                  Créé en 10 jours • Octobre 2025 • BZ inc.
                </p>
                {lastAnalysisDate && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-purple-300">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap border-b border-indigo-500/30 bg-slate-900/50">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition-all flex-1 min-w-[200px] ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-b-2 border-indigo-400'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.icon}
              {tab.name}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="prisme-content-scroll p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
          
          {/* TAB 0: LES 5 BIG IDEAS */}
          {activeTab === 0 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 mb-3">
                  Les 5 Facettes de la Puissance de PRIsME
                </h3>
                <p className="text-gray-300 text-lg max-w-3xl mx-auto">
                  PRIsME est la plateforme interne exclusive de BZ Inc., conçue pour l'excellence organisationnelle et l'excellence TI. 
                  Comme un diamant, PRIsME révèle sa puissance sous différents angles. 
                  Chaque BIG IDEA touche une réalité différente de BZ Inc., résout un problème unique à notre organisation.
                </p>
              </div>

              {/* BIG IDEA 1: Pour le Propriétaire/Président */}
              <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-xl p-6 border border-emerald-500/30">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-emerald-500/20 rounded-lg">
                    <Shield className="w-8 h-8 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-emerald-400 font-semibold uppercase tracking-wide mb-1">BIG IDEA #1 • Pour le Leadership BZ Inc.</div>
                    <h4 className="text-2xl font-bold text-white mb-2">
                      PRIsME immortalise la vision et les valeurs de BZ Inc.
                    </h4>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      L'ADN organisationnel de BZ Inc. est codifié pour toujours. Nos valeurs, notre vision, notre excellence TI 
                      sont préservées et transmises. BZ Inc. ne dépend plus d'une seule personne, mais porte ses valeurs pour toujours.
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-red-400 font-bold mb-2">❌ AVANT PRIsME:</div>
                    <div className="text-sm text-gray-300">Le savoir de BZ Inc. part avec les gens. Les employés devinent les standards. La vision d'excellence TI se perd dans les transitions.</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-green-400 font-bold mb-2">✅ AVEC PRIsME:</div>
                    <div className="text-sm text-gray-300">L'ADN de BZ Inc. est codifié. Nos valeurs d'excellence TI vivent dans le système. BZ Inc. scale sans perdre son identité.</div>
                  </div>
                </div>
              </div>

              {/* BIG IDEA 2: Pour les Employés */}
              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-blue-500/20 rounded-lg">
                    <Users className="w-8 h-8 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-blue-400 font-semibold uppercase tracking-wide mb-1">BIG IDEA #2 • Pour les Équipes BZ Inc.</div>
                    <h4 className="text-2xl font-bold text-white mb-2">
                      PRIsME élimine la confusion et standardise l'excellence TI
                    </h4>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      Chaque membre de l'équipe BZ Inc. sait exactement quoi faire, comment le faire selon nos standards d'excellence TI, et pourquoi. 
                      Fini le brouillard. Place à la clarté absolue et à l'excellence constante.
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-red-400 font-bold mb-2">❌ AVANT PRIsME:</div>
                    <div className="text-sm text-gray-300">"Personne ne m'a montré les standards BZ Inc." Formation orale qui se perd. Chacun fait à sa manière. Qualité variable.</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-green-400 font-bold mb-2">✅ AVEC PRIsME:</div>
                    <div className="text-sm text-gray-300">Tous les standards BZ Inc. sont documentés, vivants, accessibles. Onboarding TI 10x plus rapide. Excellence garantie.</div>
                  </div>
                </div>
              </div>

              {/* BIG IDEA 3: Pour les Clients */}
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-purple-500/20 rounded-lg">
                    <Target className="w-8 h-8 text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-purple-400 font-semibold uppercase tracking-wide mb-1">BIG IDEA #3 • Pour les Clients de BZ Inc.</div>
                    <h4 className="text-2xl font-bold text-white mb-2">
                      PRIsME garantit l'excellence TI constante
                    </h4>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      Chaque interaction avec un client de BZ Inc. est prévisible, professionnelle, et parfaite selon nos standards d'excellence TI. 
                      Peu importe quel membre de l'équipe BZ Inc. répond, la qualité est constante et exceptionnelle.
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-red-400 font-bold mb-2">❌ AVANT PRIsME:</div>
                    <div className="text-sm text-gray-300">Expérience incohérente avec les clients. "Ça dépend qui tu as au téléphone." Qualité variable. Client confus sur les standards BZ Inc.</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-green-400 font-bold mb-2">✅ AVEC PRIsME:</div>
                    <div className="text-sm text-gray-300">Standard d'excellence TI de BZ Inc. codifié. Chaque membre de l'équipe livre la même qualité exceptionnelle. Client en confiance totale.</div>
                  </div>
                </div>
              </div>

              {/* BIG IDEA 4: Pour l'Excellence TI de BZ Inc. */}
              <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl p-6 border border-orange-500/30">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-orange-500/20 rounded-lg">
                    <Zap className="w-8 h-8 text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-orange-400 font-semibold uppercase tracking-wide mb-1">BIG IDEA #4 • Pour l'Excellence TI de BZ Inc.</div>
                    <h4 className="text-2xl font-bold text-white mb-2">
                      PRIsME transforme le chaos TI en orchestration d'excellence
                    </h4>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      PRIsME est conçu spécifiquement pour l'excellence TI de BZ Inc. De pompier réactif à architecte proactif. 
                      De "je règle des urgences" à "je bâtis des systèmes d'excellence pour BZ Inc.".
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-red-400 font-bold mb-2">❌ AVANT PRIsME:</div>
                    <div className="text-sm text-gray-300">Réactif. Éteindre des feux. Documentation inexistante. "Ça marche comment déjà?" Tribal knowledge. Standards TI variables.</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-green-400 font-bold mb-2">✅ AVEC PRIsME:</div>
                    <div className="text-sm text-gray-300">Proactif. Systèmes TI de BZ Inc. documentés. Processus d'excellence clairs. Gouvernance mature. ITIL/COBIT/NIST appliqués à BZ Inc.</div>
                  </div>
                </div>
              </div>

              {/* BIG IDEA 5: Pour l'Avenir */}
              <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-indigo-500/20 rounded-lg">
                    <TrendingUp className="w-8 h-8 text-indigo-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-indigo-400 font-semibold uppercase tracking-wide mb-1">BIG IDEA #5 • Pour l'Avenir de BZ Inc.</div>
                    <h4 className="text-2xl font-bold text-white mb-2">
                      PRIsME apprend et s'améliore automatiquement pour BZ Inc.
                    </h4>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      BZ Inc. devient plus intelligente chaque jour grâce à PRIsME. L'IA capture, structure, et optimise en continu 
                      nos processus TI, nos standards d'excellence, et notre façon de servir nos clients.
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-red-400 font-bold mb-2">❌ AVANT PRIsME:</div>
                    <div className="text-sm text-gray-300">Connaissances figées chez BZ Inc. Documentation périmée. Amélioration manuelle lente. "On a toujours fait comme ça chez BZ."</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-green-400 font-bold mb-2">✅ AVEC PRIsME:</div>
                    <div className="text-sm text-gray-300">Système vivant pour BZ Inc. Amélioration continue automatique de nos standards TI. L'IA suggère des optimisations spécifiques à BZ Inc. Évolution perpétuelle.</div>
                  </div>
                </div>
              </div>

              {/* Conclusion des 5 BIG IDEAS */}
              <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-pink-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
                <div className="text-center">
                  <h4 className="text-2xl font-bold text-white mb-3">
                    5 Angles. 1 Révolution. PRIsME pour BZ Inc.
                  </h4>
                  <p className="text-gray-300 text-lg">
                    Chaque BIG IDEA est puissante seule. Ensemble, elles transforment BZ Inc. de fond en comble, 
                    garantissant l'excellence TI et organisationnelle pour les années à venir.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* TAB 1: L'ORIGINE */}
          {activeTab === 1 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400 mb-3">
                  L'Origine de PRIsME
                </h3>
                <p className="text-gray-300 text-lg">
                  Tout a commencé par un appel. Un problème. Et une décision.
                </p>
              </div>

              {/* Timeline */}
              <div className="space-y-6">
                {/* Appel #1 */}
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-yellow-500/20 border-2 border-yellow-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-yellow-400 font-bold">1</span>
                    </div>
                    <div className="w-0.5 h-full bg-gradient-to-b from-yellow-500 to-green-500 my-2"></div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-6 flex-1 border border-yellow-500/30">
                    <div className="text-sm text-yellow-400 font-semibold mb-2">Début Octobre 2025 • 2 semaines avant</div>
                    <h4 className="text-xl font-bold text-white mb-3">Premier appel: Problème de Charge de Projet</h4>
                    <p className="text-gray-300">
                      Jean-Pierre Leclerc contacte Dany Bourassa pour un problème de gestion de charge de projet chez BZ Inc. 
                      Un enjeu qui persiste, qui ralentit l'efficacité.
                    </p>
                  </div>
                </div>

                {/* Appel #2 - LE DÉCLIC */}
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-400 font-bold">2</span>
                    </div>
                    <div className="w-0.5 h-full bg-gradient-to-b from-green-500 to-indigo-500 my-2"></div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg p-6 flex-1 border-2 border-green-500/50 shadow-lg shadow-green-500/20">
                    <div className="text-sm text-green-400 font-semibold mb-2">14 Octobre 2025 • L'APPEL QUI A TOUT CHANGÉ</div>
                    <h4 className="text-2xl font-bold text-white mb-3">Deuxième appel: Problème de Formation Prime</h4>
                    <p className="text-gray-300 mb-4">
                      Jean-Pierre Leclerc rappelle Dany Bourassa. Cette fois, c'est un problème de <strong className="text-white">formation et d'éducation d'un Prime</strong> chez BZ Inc. 
                      Comment former efficacement? Comment transmettre le savoir? Comment ne pas perdre la connaissance?
                    </p>
                    <div className="bg-slate-900/50 rounded-lg p-4 border-l-4 border-green-500">
                      <p className="text-gray-300 italic">
                        "Ce problème de formation s'aligne parfaitement avec le problème de charge de projet d'avant. 
                        Tout est lié. Tout est systémique."
                      </p>
                      <p className="text-gray-400 text-sm mt-2">— Dany Bourassa</p>
                    </div>
                  </div>
                </div>

                {/* La Réponse */}
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-indigo-500/20 border-2 border-indigo-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-indigo-400 font-bold">3</span>
                    </div>
                    <div className="w-0.5 h-full bg-gradient-to-b from-indigo-500 to-purple-500 my-2"></div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-6 flex-1 border border-indigo-500/30">
                    <div className="text-sm text-indigo-400 font-semibold mb-2">14 Octobre 2025 • La Décision</div>
                    <h4 className="text-xl font-bold text-white mb-3">La Réponse</h4>
                    <div className="bg-gradient-to-r from-indigo-600/30 to-purple-600/30 rounded-lg p-4 border border-indigo-500/50">
                      <p className="text-white text-xl font-bold text-center">
                        "C'est rendu mon problème."
                      </p>
                      <p className="text-indigo-300 text-sm text-center mt-2">— Dany Bourassa</p>
                    </div>
                    <p className="text-gray-300 mt-4">
                      Ce n'était plus juste le problème de Jean-Pierre. C'était devenu le problème à résoudre. 
                      Après des années chez BZ Inc., après avoir vu tant de défis similaires, après avoir accumulé tant de savoir... 
                      Et quand quelque chose devient ton problème, tu le règles.
                    </p>
                  </div>
                </div>

                {/* La Création */}
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-purple-500/20 border-2 border-purple-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-400 font-bold">4</span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg p-6 flex-1 border-2 border-purple-500/50 shadow-lg shadow-purple-500/20">
                    <div className="text-sm text-purple-400 font-semibold mb-2">Octobre 2025 • 10 jours</div>
                    <h4 className="text-2xl font-bold text-white mb-3">PRIsME est né</h4>
                    <p className="text-gray-300 mb-4">
                      En 10 jours, tout est devenu clair dans la tête de Dany Bourassa. Comment résoudre ce problème aujourd'hui avec les technologies IA à notre disposition. 
                      Après des années d'expérience chez BZ Inc., après avoir vu tous les patterns, tous les défis, toutes les solutions... 
                      Il pouvait enfin libérer son cerveau en pondant PRIsME. Une libération totale.
                    </p>
                    <p className="text-gray-300 mb-4">
                      Tout ce qui était dans sa tête depuis toujours a été structuré. Codifié. Mis en système. 
                      L'IA permettait enfin de capturer, d'organiser, et de rendre accessible tout ce savoir accumulé. 
                      Plus besoin de tout garder en mémoire. PRIsME devenait l'extension de son cerveau.
                    </p>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold text-purple-400">10</div>
                        <div className="text-sm text-gray-400">jours de création</div>
                      </div>
                      <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold text-pink-400">∞</div>
                        <div className="text-sm text-gray-400">idées en tête</div>
                      </div>
                      <div className="bg-slate-900/50 rounded-lg p-4 text-center">
                        <div className="text-3xl font-bold text-indigo-400">1</div>
                        <div className="text-sm text-gray-400">révolution</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Le Crédit */}
              <div className="bg-gradient-to-r from-emerald-500/20 to-green-500/20 rounded-xl p-6 border-2 border-emerald-500/50">
                <div className="text-center">
                  <h4 className="text-2xl font-bold text-white mb-3">Le Mérite de l'Idée</h4>
                  <p className="text-xl text-emerald-400 font-semibold mb-2">Jean-Pierre Leclerc</p>
                  <p className="text-gray-300 mb-4">
                    Sans son appel du 14 octobre, PRIsME n'existerait pas. 
                    Il a identifié le problème. Le reste a suivi.
                  </p>
                  <div className="border-t border-emerald-500/30 pt-4 mt-4">
                    <p className="text-xl text-emerald-400 font-semibold mb-2">Dany Bourassa</p>
                    <p className="text-gray-300">
                      Après des années chez BZ Inc., après avoir accumulé tant de savoir, tant d'expérience, 
                      tant de compréhension de ce qui fonctionne et de ce qui ne fonctionne pas... 
                      Il a décidé que c'était devenu son problème à résoudre. Le moment était venu de structurer tout ça.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: LE MANIFESTE */}
          {activeTab === 2 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400 mb-3">
                  Le Manifeste PRIsME
                </h3>
                <p className="text-gray-300 text-lg">
                  Ce n'est plus un choix. C'est une réalité.
                </p>
              </div>

              {/* Le Message Fort */}
              <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-8 border border-indigo-500/30">
                <h4 className="text-2xl font-bold text-white mb-6 text-center">PRIsME Existe. PRIsME Transforme.</h4>
                
                <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 mb-4">
                      PRIsME existe.
                    </p>
                    <p className="text-gray-300 text-xl">
                      Une plateforme interne exclusive de BZ Inc., conçue pour l'excellence organisationnelle et l'excellence TI.
                    </p>
                  </div>

                  <div className="my-8 h-0.5 bg-gradient-to-r from-transparent via-indigo-500 to-transparent"></div>

                  <div className="text-center">
                    <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 mb-4">
                      PRIsME transforme.
                    </p>
                    <p className="text-gray-300 text-xl">
                      Chaque interaction, chaque processus, chaque décision devient plus claire, plus structurée, plus puissante.
                    </p>
                  </div>

                  <div className="my-8 h-0.5 bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>

                  <div className="text-center">
                    <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-400 to-red-400 mb-4">
                      Vos clients en ont besoin.
                    </p>
                    <p className="text-gray-300 text-xl">
                      L'excellence TI que PRIsME apporte à BZ Inc. se reflète directement dans la qualité de service offerte à nos clients.
                    </p>
                  </div>
                </div>

                <div className="mt-8 bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-pink-600/20 rounded-lg p-6 border border-indigo-500/30">
                  <p className="text-center text-gray-300 text-lg">
                    <strong className="text-white">PRIsME structure.</strong> <strong className="text-indigo-400">PRIsME simplifie.</strong> <strong className="text-purple-400">PRIsME clarifie.</strong> <strong className="text-pink-400">PRIsME transforme.</strong>
                  </p>
                </div>
              </div>

              {/* L'Arbre - Métaphore */}
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30">
                <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  🌳 La Métaphore de l'Arbre
                </h4>
                <p className="text-gray-300 text-lg mb-6">
                  Le cerveau est faible et limité. On doit lui placer l'information dans des petites cases. 
                  PRIsME structure comme un arbre.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-amber-500">
                    <div className="text-amber-400 font-bold mb-2">🌲 Le Tronc</div>
                    <p className="text-gray-300">
                      La fondation. Les principes. Les valeurs. Ce qui ne change jamais. 
                      <strong className="text-white"> Commencez ici. Toujours.</strong>
                    </p>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-green-500">
                    <div className="text-green-400 font-bold mb-2">🌿 Les Branches</div>
                    <p className="text-gray-300">
                      Les catégories. Les domaines. Les grandes divisions. 
                      <strong className="text-white"> Ensuite, explorez les branches.</strong>
                    </p>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-emerald-500">
                    <div className="text-emerald-400 font-bold mb-2">🍃 Les Feuilles</div>
                    <p className="text-gray-300">
                      Les détails. Les processus spécifiques. Les millions de petites choses. 
                      <strong className="text-white"> Finalement, les feuilles deviennent évidentes.</strong>
                    </p>
                  </div>
                </div>

                <div className="mt-6 bg-green-500/10 rounded-lg p-4 border border-green-500/30">
                  <p className="text-gray-300 text-center">
                    <strong className="text-green-400">Beaucoup plus simple de comprendre le tronc et les branches 
                    que les millions de feuilles.</strong> PRIsME structure. PRIsME simplifie. PRIsME clarifie.
                  </p>
                </div>
              </div>

              {/* La Propagation */}
              <div className="bg-gradient-to-br from-indigo-600/20 to-purple-600/20 rounded-xl p-8 border-2 border-indigo-500/50 shadow-lg shadow-indigo-500/20">
                <h4 className="text-2xl font-bold text-white mb-6 text-center">PRIsME va se propager</h4>
                
                <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                  <p className="text-center text-xl">
                    <strong className="text-white">PRIsME va se propager avec BZ Inc.</strong>
                  </p>

                  <p className="text-center text-lg">
                    Pour donner la puissance technologique à nos clients et les pousser vers l'excellence grâce à PRIsME.
                  </p>

                  <div className="bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-lg p-6 border border-indigo-500/30 my-6">
                    <p className="text-center text-gray-300 text-lg">
                      Ensemble, nous transformons l'excellence TI de BZ Inc. en valeur tangible pour nos clients.
                    </p>
                    <p className="text-center text-gray-300 text-lg mt-3">
                      <strong className="text-white">Tous ensemble.</strong> <strong className="text-indigo-400">Plus fort.</strong> <strong className="text-purple-400">Plus rapide.</strong> <strong className="text-pink-400">Plus d'impact.</strong>
                    </p>
                  </div>

                  <div className="my-8 h-0.5 bg-gradient-to-r from-transparent via-indigo-500 to-transparent"></div>

                  <p className="text-center text-xl">
                    <strong className="text-white">PRIsME existe déjà.</strong>
                  </p>

                  <p className="text-center text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400">
                    PRIsME transforme déjà.
                  </p>

                  <p className="text-center text-lg text-gray-300 mt-4">
                    Rejoignez-nous dans cette transformation vers l'excellence.
                  </p>
                </div>
              </div>

              {/* Le Contrôle */}
              <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-xl p-6 border border-indigo-500/30">
                <h4 className="text-xl font-bold text-white mb-4 text-center">
                  PRIsME vous donne le contrôle
                </h4>
                <p className="text-gray-300 text-center text-lg">
                  Pour emmener l'excellence TI chez nos clients. Utilisez la structure de PRIsME comme expliqué. 
                  Commencez par le tronc. Explorez les branches. Rendez-vous aux feuilles.
                </p>
                <p className="text-center text-xl font-bold text-indigo-400 mt-4">
                  PRIsME vous donne ce contrôle. Structuré. Puissant. Clair.
                </p>
              </div>
            </div>
          )}

          {/* TAB 3: POURQUOI PRIsME */}
          {activeTab === 3 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 mb-3">
                  Pourquoi PRIsME?
                </h3>
                <p className="text-gray-300 text-lg">
                  L'histoire derrière le nom. La signification. La puissance.
                </p>
              </div>

              {/* L'Origine du Nom */}
              <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-xl p-8 border border-emerald-500/30">
                <h4 className="text-2xl font-bold text-white mb-6 text-center">L'Origine du Nom PRIsME</h4>
                
                <div className="space-y-6">
                  {/* PRIME */}
                  <div className="bg-slate-800/50 rounded-lg p-6 border-l-4 border-emerald-500">
                    <h5 className="text-2xl font-bold text-emerald-400 mb-3">PRIME</h5>
                    <p className="text-gray-300 text-lg leading-relaxed mb-4">
                      Depuis 25 ans, <strong className="text-white">PRIME</strong> est le terme utilisé à l'interne chez BZ Inc. 
                      pour désigner le technicien haut de gamme de référence pour un client. 
                      C'est le directeur TI du client, l'expert de confiance, celui qui incarne l'excellence technique.
                    </p>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      <strong className="text-emerald-400">PRIME est unique à BZ Inc.</strong> C'est un terme qui fait partie de notre ADN, 
                      de notre histoire, de notre façon de voir l'excellence TI.
                    </p>
                  </div>

                  {/* Le "S" */}
                  <div className="bg-slate-800/50 rounded-lg p-6 border-l-4 border-indigo-500">
                    <h5 className="text-2xl font-bold text-indigo-400 mb-3">Le "S" de Profondeur</h5>
                    <p className="text-gray-300 text-lg leading-relaxed mb-4">
                      En ajoutant le <strong className="text-white">"s"</strong> à PRIME, nous créons <strong className="text-indigo-400">PRIsME</strong>.
                    </p>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      Ce <strong className="text-white">"s"</strong> démontre toute la <strong className="text-indigo-400">profondeur</strong> de notre approche. 
                      Il symbolise que nous pensons à <strong className="text-white">tous les angles</strong>, à toutes les dimensions, 
                      à toutes les facettes de l'excellence TI. Ce n'est plus un seul PRIME, mais une vision systémique 
                      qui englobe tous les aspects de l'organisation.
                    </p>
                  </div>

                  {/* AI */}
                  <div className="bg-slate-800/50 rounded-lg p-6 border-l-4 border-blue-500">
                    <h5 className="text-2xl font-bold text-blue-400 mb-3">L'Intelligence Artificielle</h5>
                    <p className="text-gray-300 text-lg leading-relaxed mb-4">
                      Bien que l'<strong className="text-blue-400">IA</strong> (Intelligence Artificielle) ne soit pas dans le nom PRIsME, 
                      elle est au cœur de la plateforme. C'est le catalyseur qui permet de structurer, d'organiser et d'amplifier 
                      toute cette profondeur. Elle capture, analyse, optimise et suggère.
                    </p>
                    <p className="text-gray-300 text-lg leading-relaxed">
                      <strong className="text-blue-400">L'IA amplifie l'humain, ne le remplace pas.</strong> Elle transforme 
                      le savoir accumulé en système vivant, accessible et évolutif. C'est la puissance technologique qui rend PRIsME possible.
                    </p>
                  </div>
                </div>
              </div>

              {/* L'Union */}
              <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-pink-600/20 rounded-xl p-8 border-2 border-indigo-500/50">
                <h4 className="text-3xl font-bold text-center text-white mb-6">
                  PRIME + "S" = PRIsME
                </h4>
                <p className="text-gray-300 text-xl text-center leading-relaxed mb-6">
                  Plateforme interne BZ Inc. pour l'excellence organisationnelle et l'excellence TI
                </p>
                <p className="text-gray-300 text-lg text-center leading-relaxed mb-4">
                  PRIsME, c'est l'union de <strong className="text-emerald-400">PRIME</strong> (notre histoire, notre excellence), 
                  et du <strong className="text-indigo-400">"s"</strong> (la profondeur, tous les angles).
                </p>
                <p className="text-gray-300 text-lg text-center leading-relaxed">
                  Alimentée par la <strong className="text-blue-400">puissance de l'IA</strong>, PRIsME transforme cette excellence 
                  en système vivant, structuré et évolutif, appliqué spécifiquement à l'excellence TI de BZ Inc.
                </p>
              </div>

              {/* Ce que ça signifie concrètement */}
              <div className="space-y-4">
                <h4 className="text-2xl font-bold text-white text-center mb-6">
                  Concrètement, PRIsME c'est:
                </h4>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-indigo-500">
                    <h5 className="text-indigo-400 font-bold mb-2">📐 Voir sous tous les angles</h5>
                    <p className="text-sm text-gray-300">Votre organisation déconstruite, analysée, comprise dans sa totalité.</p>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-purple-500">
                    <h5 className="text-purple-400 font-bold mb-2">🧠 Structurer l'intelligence</h5>
                    <p className="text-sm text-gray-300">Chaque connaissance, processus, valeur codifié et accessible.</p>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-pink-500">
                    <h5 className="text-pink-400 font-bold mb-2">🔄 Évoluer en continu</h5>
                    <p className="text-sm text-gray-300">Un système vivant qui apprend et s'améliore automatiquement.</p>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-cyan-500">
                    <h5 className="text-cyan-400 font-bold mb-2">💎 Immortaliser les valeurs</h5>
                    <p className="text-sm text-gray-300">Votre ADN organisationnel préservé et transmis éternellement.</p>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-emerald-500">
                    <h5 className="text-emerald-400 font-bold mb-2">🎯 Clarifier l'exécution</h5>
                    <p className="text-sm text-gray-300">Chaque employé sait exactement quoi faire, comment, et pourquoi.</p>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-5 border-l-4 border-orange-500">
                    <h5 className="text-orange-400 font-bold mb-2">🚀 Scaler l'excellence</h5>
                    <p className="text-sm text-gray-300">Grandir sans perdre qualité, cohérence, ou identité.</p>
                  </div>
                </div>
              </div>

              {/* Le Nom parfait */}
              <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                <div className="text-center">
                  <h4 className="text-2xl font-bold text-white mb-3">
                    Un nom qui porte notre histoire et notre vision
                  </h4>
                  <p className="text-gray-300 text-lg">
                    PRIsME capture parfaitement l'essence de BZ Inc.: notre excellence technique (PRIME), 
                    notre vision systémique et profonde (le "s"), et la puissance de l'IA pour structurer et amplifier tout cela.
                  </p>
                  <p className="text-indigo-400 font-semibold text-xl mt-4">
                    Court. Mémorable. Puissant. Unique à BZ Inc. PRIsME.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: ARCHITECTURE & GOUVERNANCE */}
          {activeTab === 4 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 mb-3">
                  Architecture & Gouvernance PRIsME
                </h3>
                <p className="text-gray-300 text-lg">
                  Le concept général de PRIsME : Master, départements, clients et gouvernance
                </p>
              </div>

              {/* Concept Général */}
              <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-pink-600/20 rounded-xl p-8 border-2 border-indigo-500/50">
                <h4 className="text-2xl font-bold text-white mb-6 text-center flex items-center justify-center gap-3">
                  <Crown className="w-8 h-8 text-yellow-400" />
                  PRIsME Master : La Source
                </h4>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-6 border border-indigo-500/30">
                    <h5 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Brain className="w-6 h-6 text-indigo-400" />
                      Le Master PRIsME BZ
                    </h5>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Source unique</strong> pour former, guider, créer documents et livrables</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Cadres, standards, concepts BZ</strong> utilisés pour bâtir tous les PRIsME (employés et clients)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Base de données partagée</strong> qui alimente les prismes employés et clients</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                        <span>Connecté à <strong className="text-white">TactiCE</strong> et à l’outil <strong className="text-white">RH</strong> pour refléter la réalité BZ en continu</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
                    <h5 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Network className="w-6 h-6 text-purple-400" />
                      PRIsME Clients
                    </h5>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Chaque client</strong> a son propre PRIsME</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Données de départ pré-configurées</strong> pour personnalisation rapide</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Standards BZ toujours respectés</strong> grâce au Master</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Prime</strong> est responsable du prisme de chaque client (mise à jour, cohérence, suivi)</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-xl p-6 border border-emerald-500/30">
                    <h5 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Users className="w-6 h-6 text-emerald-400" />
                      PRIsME Employés
                    </h5>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Un prisme par employé</strong>, nourri par PRIsME Master, TactiCE et l’outil RH</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span>Le gestionnaire est <strong className="text-white">responsable</strong> de la mise à jour et du coaching lié au prisme de son employé</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span>Le prisme employé personnalise la relation interne, comme le prisme client personnalise la relation externe</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Diagramme de Flux Master → Clients */}
              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-8 border border-blue-500/30">
                <h4 className="text-2xl font-bold text-white mb-6 text-center flex items-center justify-center gap-3">
                  <ArrowRight className="w-8 h-8 text-blue-400" />
                  Flux Master → Clients
                </h4>
                <div className="space-y-6">
                  {/* PRIsME Master */}
                  <div className="flex justify-center">
                    <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-6 shadow-2xl border-4 border-yellow-400 max-w-md w-full">
                      <div className="text-center">
                        <Crown className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
                        <h5 className="text-2xl font-bold text-white mb-2">PRIsME Master BZ</h5>
                        <p className="text-indigo-100 text-sm">Cadres • Standards • Concepts</p>
                        <p className="text-indigo-100 text-sm">Base de données partagée</p>
                      </div>
                    </div>
                  </div>

                  {/* Flèches vers Clients & Employés */}
                  <div className="flex flex-col items-center gap-4">
                    <div className="flex items-center gap-2 text-blue-400">
                      <ArrowRight className="w-8 h-8" />
                      <span className="text-lg font-semibold">Propagation</span>
                      <ArrowRight className="w-8 h-8" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 w-full">
                      {/* PRIsME Clients */}
                      <div className="bg-gradient-to-br from-purple-600/80 to-pink-600/80 rounded-xl p-4 shadow-lg border-2 border-purple-400">
                        <div className="text-center">
                          <Building2 className="w-8 h-8 text-white mx-auto mb-2" />
                          <h6 className="text-lg font-bold text-white">PRIsME Clients</h6>
                          <p className="text-purple-100 text-xs mt-1">Personnalisés</p>
                          <p className="text-purple-100 text-xs">Données de départ pré-configurées</p>
                        </div>
                      </div>
                      {/* PRIsME Employés */}
                      <div className="bg-gradient-to-br from-emerald-600/80 to-green-600/80 rounded-xl p-4 shadow-lg border-2 border-emerald-400">
                        <div className="text-center">
                          <Users className="w-8 h-8 text-white mx-auto mb-2" />
                          <h6 className="text-lg font-bold text-white">PRIsME Employés</h6>
                          <p className="text-emerald-100 text-xs mt-1">Par employé</p>
                          <p className="text-emerald-100 text-xs">Données de départ pré-configurées</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Diagramme de Gouvernance */}
              <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-xl p-8 border border-emerald-500/30">
                <h4 className="text-2xl font-bold text-white mb-6 text-center flex items-center justify-center gap-3">
                  <Users className="w-8 h-8 text-emerald-400" />
                  Gouvernance PRIsME
                </h4>
                
                {/* Dany - Imputable Overall */}
                <div className="flex justify-center mb-8">
                  <div className="bg-gradient-to-br from-yellow-600 to-orange-600 rounded-2xl p-6 shadow-2xl border-4 border-yellow-400 max-w-sm w-full">
                    <div className="text-center">
                      <Crown className="w-10 h-10 text-yellow-300 mx-auto mb-2" />
                      <h5 className="text-xl font-bold text-white mb-1">Dany Bourassa</h5>
                      <p className="text-yellow-100 text-sm font-semibold">Imputable Overall PRIsME</p>
                      <p className="text-yellow-100 text-xs mt-2">
                        Vérifie cohérence globale • Approuve le final • Veto sur les cas sensibles (employés & clients)
                      </p>
                    </div>
                  </div>
                </div>

                {/* 3 Colonnes : Dany | Gestionnaires | Dany/François */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  {/* Colonne 1: Dany */}
                  <div className="flex flex-col">
                    <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl p-5 shadow-xl border-2 border-blue-400 w-full mb-4">
                      <div className="text-center">
                        <Users className="w-8 h-8 text-blue-200 mx-auto mb-2" />
                        <h6 className="text-lg font-bold text-white mb-1">Dany Bourassa</h6>
                        <p className="text-blue-100 text-xs font-semibold">Imputable</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-blue-500/20 rounded-lg p-3 border border-blue-400/30">
                        <p className="text-white text-sm font-semibold mb-2">Ventes</p>
                        <p className="text-blue-200 text-xs">Antoine Giroux (Dir.)</p>
                      </div>
                      <div className="bg-blue-500/20 rounded-lg p-3 border border-blue-400/30">
                        <p className="text-white text-sm font-semibold mb-2">Marketing</p>
                        <p className="text-blue-200 text-xs">Rosalie Emond (Resp.)</p>
                      </div>
                      <div className="bg-blue-500/20 rounded-lg p-3 border border-blue-400/30">
                        <p className="text-white text-sm font-semibold mb-2">RH</p>
                        <p className="text-blue-200 text-xs">Dany Bourassa (Intérim)</p>
                      </div>
                      <div className="bg-blue-500/20 rounded-lg p-3 border border-blue-400/30">
                        <p className="text-white text-sm font-semibold mb-2">Cybersécurité</p>
                        <p className="text-blue-200 text-xs">Francis Marquis (Dir.)</p>
                      </div>
                    </div>
                  </div>

                  {/* Colonne 2: Gestionnaires (au centre) */}
                  <div className="flex flex-col">
                    <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl p-5 shadow-xl border-2 border-green-400 w-full mb-4">
                      <div className="text-center">
                        <Users className="w-8 h-8 text-green-200 mx-auto mb-2" />
                        <h6 className="text-lg font-bold text-white mb-1">Gestionnaires</h6>
                        <p className="text-green-100 text-xs font-semibold">Responsables des PRIsME Employés</p>
                        <p className="text-green-100 text-xs mt-1">Coaching, suivi, mise à jour des prismes de leurs équipes</p>
                      </div>
                    </div>
                  </div>

                  {/* Colonne 3: Dany avec François */}
                  <div className="flex flex-col">
                    <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl p-5 shadow-xl border-2 border-indigo-400 w-full mb-4">
                      <div className="text-center">
                        <Crown className="w-8 h-8 text-indigo-200 mx-auto mb-2" />
                        <h6 className="text-lg font-bold text-white mb-1">Dany Bourassa</h6>
                        <p className="text-indigo-100 text-xs font-semibold">Imputable</p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-4 shadow-xl border-2 border-purple-400 w-full mb-4">
                      <div className="text-center">
                        <Shield className="w-6 h-6 text-purple-200 mx-auto mb-1" />
                        <h6 className="text-base font-bold text-white mb-1">François Delisle</h6>
                        <p className="text-purple-100 text-xs font-semibold">Bras Droit de Dany</p>
                        <p className="text-purple-100 text-xs mt-1">Assiste validation</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-indigo-500/20 rounded-lg p-3 border border-indigo-400/30">
                        <p className="text-white text-sm font-semibold mb-2">Infonuagique</p>
                        <p className="text-indigo-200 text-xs">Richard Delisle (Dir.)</p>
                      </div>
                      <div className="bg-indigo-500/20 rounded-lg p-3 border border-indigo-400/30">
                        <p className="text-white text-sm font-semibold mb-2">Administration</p>
                        <p className="text-indigo-200 text-xs">Mélissa Pelletier (Dir.)</p>
                        <p className="text-indigo-200 text-xs">Emilie Quintal (Assist.)</p>
                      </div>
                      <div className="bg-indigo-500/20 rounded-lg p-3 border border-indigo-400/30">
                        <p className="text-white text-sm font-semibold mb-2">Service TI</p>
                        <p className="text-indigo-200 text-xs">Jean-Pierre Leclerc (Dir.)</p>
                        <p className="text-indigo-200 text-xs">Eric Hains, Vincent Bourassa,</p>
                        <p className="text-indigo-200 text-xs">Jean-Michel Caouette (Assist.)</p>
                      </div>
                      <div className="bg-indigo-500/20 rounded-lg p-3 border border-indigo-400/30">
                        <p className="text-white text-sm font-semibold mb-2">Programmation</p>
                        <p className="text-indigo-200 text-xs">Eric Durand (Dir.)</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gouvernance spécifique PRIsME Employés & Clients */}
                <div className="mt-8 grid md:grid-cols-2 gap-6">
                  <div className="bg-emerald-900/40 rounded-xl p-5 border border-emerald-500/40">
                    <h5 className="text-lg font-bold text-white mb-3">Gouvernance des PRIsME Employés</h5>
                    <ul className="space-y-2 text-gray-200 text-sm">
                      <li>• <strong className="text-white">Gestionnaire</strong> : responsable principal du prisme de chaque employé (contenu, mise à jour, cohérence).</li>
                      <li>• <strong className="text-white">CE</strong> : valide les grands principes, s’assure que les prismes respectent les standards BZ.</li>
                      <li>• <strong className="text-white">Dany</strong> : garde une vue globale, intervient sur les cas sensibles ou stratégiques.</li>
                    </ul>
                  </div>
                  <div className="bg-purple-900/40 rounded-xl p-5 border border-purple-500/40">
                    <h5 className="text-lg font-bold text-white mb-3">Gouvernance des PRIsME Clients</h5>
                    <ul className="space-y-2 text-gray-200 text-sm">
                      <li>• <strong className="text-white">Prime</strong> : responsable du prisme de chaque client (mise à jour, priorisation, vision 360).</li>
                      <li>• <strong className="text-white">CE</strong> : veille à la conformité, à l’alignement avec la vision BZ et aux enjeux stratégiques.</li>
                      <li>• <strong className="text-white">Dany</strong> : tranche en dernier recours sur les décisions critiques.</li>
                    </ul>
                  </div>
                </div>

                {/* Matrice RACI simplifiée */}
                <div className="mt-8 bg-slate-900/60 rounded-xl p-5 border border-slate-700/60">
                  <h5 className="text-lg font-bold text-white mb-4 text-center">Matrice RACI – Gouvernance PRIsME</h5>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs md:text-sm text-gray-200 border-collapse">
                      <thead>
                        <tr className="bg-slate-800/80">
                          <th className="border border-slate-700 px-3 py-2 text-left">Activité</th>
                          <th className="border border-slate-700 px-3 py-2 text-center">Dany</th>
                          <th className="border border-slate-700 px-3 py-2 text-center">CE</th>
                          <th className="border border-slate-700 px-3 py-2 text-center">Gestionnaires</th>
                          <th className="border border-slate-700 px-3 py-2 text-center">Prime</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-slate-700 px-3 py-2">Gouvernance PRIsME Employés</td>
                          <td className="border border-slate-700 px-3 py-2 text-center">A / Veto</td>
                          <td className="border border-slate-700 px-3 py-2 text-center">C</td>
                          <td className="border border-slate-700 px-3 py-2 text-center">R</td>
                          <td className="border border-slate-700 px-3 py-2 text-center"></td>
                        </tr>
                        <tr className="bg-slate-800/40">
                          <td className="border border-slate-700 px-3 py-2">Gouvernance PRIsME Clients</td>
                          <td className="border border-slate-700 px-3 py-2 text-center">A / Veto</td>
                          <td className="border border-slate-700 px-3 py-2 text-center">C</td>
                          <td className="border border-slate-700 px-3 py-2 text-center">I</td>
                          <td className="border border-slate-700 px-3 py-2 text-center">R</td>
                        </tr>
                        <tr>
                          <td className="border border-slate-700 px-3 py-2">Maintien du PRIsME Master BZ</td>
                          <td className="border border-slate-700 px-3 py-2 text-center">A</td>
                          <td className="border border-slate-700 px-3 py-2 text-center">R</td>
                          <td className="border border-slate-700 px-3 py-2 text-center">C</td>
                          <td className="border border-slate-700 px-3 py-2 text-center">I</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] md:text-xs text-slate-400 mt-3 text-center">
                    R = Responsable • A = Imputable • C = Consulté • I = Informé
                  </p>
                </div>
              </div>

              {/* Processus de Création */}
              <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl p-8 border border-cyan-500/30">
                <h4 className="text-2xl font-bold text-white mb-6 text-center flex items-center justify-center gap-3">
                  <Workflow className="w-8 h-8 text-cyan-400" />
                  Processus de Création de Contenu
                </h4>
                <div className="space-y-4">
                    {[
                    { step: 1, title: "Directeur/Gestionnaire", desc: "Crée le contenu de son département", circleBg: "bg-blue-500/20", circleBorder: "border-blue-500", circleText: "text-blue-400", cardBg: "from-blue-500/10 to-blue-600/10", cardBorder: "border-blue-500/30", arrowColor: "text-blue-400" },
                    { step: 2, title: "Imputable (CE)", desc: "Valide le contenu avant publication", circleBg: "bg-purple-500/20", circleBorder: "border-purple-500", circleText: "text-purple-400", cardBg: "from-purple-500/10 to-purple-600/10", cardBorder: "border-purple-500/30", arrowColor: "text-purple-400" },
                    { step: 3, title: "Dany Bourassa", desc: "Vérifie la cohérence overall", circleBg: "bg-yellow-500/20", circleBorder: "border-yellow-500", circleText: "text-yellow-400", cardBg: "from-yellow-500/10 to-yellow-600/10", cardBorder: "border-yellow-500/30", arrowColor: "text-yellow-400" },
                    { step: 4, title: "Publication", desc: "Contenu publié dans PRIsME Master", circleBg: "bg-green-500/20", circleBorder: "border-green-500", circleText: "text-green-400", cardBg: "from-green-500/10 to-green-600/10", cardBorder: "border-green-500/30", arrowColor: "text-green-400" },
                    { step: 5, title: "Propagation", desc: "Disponible pour les PRIsME Clients et les PRIsME Employés", circleBg: "bg-cyan-500/20", circleBorder: "border-cyan-500", circleText: "text-cyan-400", cardBg: "from-cyan-500/10 to-cyan-600/10", cardBorder: "border-cyan-500/30", arrowColor: "text-cyan-400" },
                    ].map((s) => (
                    <div key={s.step} className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full ${s.circleBg} border-2 ${s.circleBorder} flex items-center justify-center flex-shrink-0`}>
                        <span className={`${s.circleText} font-bold text-xl`}>{s.step}</span>
                      </div>
                      <div className={`bg-gradient-to-br ${s.cardBg} rounded-xl p-4 border ${s.cardBorder} flex-1`}>
                        <h6 className="text-lg font-bold text-white mb-1">{s.title}</h6>
                        <p className="text-gray-300 text-sm">{s.desc}</p>
                      </div>
                      {s.step < 5 && (
                        <ArrowRight className={`w-6 h-6 ${s.arrowColor} flex-shrink-0`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: DEVENEZ AMBASSADEURS */}
          {activeTab === 5 && (
            <div className="space-y-8">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400 mb-3">
                  Devenez les Ambassadeurs de PRIsME
                </h3>
                <p className="text-gray-300 text-lg">
                  La vraie magie, c'est ce que VOUS allez en faire.
                </p>
              </div>

              {/* Le Constat */}
              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                <h4 className="text-2xl font-bold text-white mb-4">PRIsME existe. Et maintenant?</h4>
                <p className="text-gray-300 text-lg leading-relaxed mb-4">
                  PRIsME est une plateforme puissante, mais ce n'est qu'un <strong className="text-white">début</strong>. 
                  Le potentiel est <strong className="text-cyan-400">INFINI</strong>. Les possibilités sont <strong className="text-blue-400">ILLIMITÉES</strong>.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Pour que PRIsME vive, pour qu'il transforme vraiment l'excellence TI de BZ Inc. et de nos clients...
                </p>
              </div>

              {/* Il faut VOUS */}
              <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-xl p-8 border-2 border-purple-500/50 shadow-lg shadow-purple-500/20">
                <div className="text-center mb-6">
                  <h4 className="text-3xl font-bold text-white mb-3">
                    Il faut VOUS.
                  </h4>
                  <p className="text-xl text-purple-400">
                    Vous êtes les ambassadeurs. Les génies. Les propagateurs.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="bg-slate-900/50 rounded-lg p-6">
                    <h5 className="text-xl font-bold text-purple-400 mb-3">🎯 Votre Mission</h5>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <span className="text-green-400">✓</span>
                        <span><strong className="text-white">Propager la bonne nouvelle</strong> - Parlez de PRIsME à vos clients</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-400">✓</span>
                        <span><strong className="text-white">Mettre cette puissance chez tous vos clients</strong> - Ils en ont besoin</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-400">✓</span>
                        <span><strong className="text-white">Devenir les génies</strong> - Vous allez être ceux qui transforment l'industrie</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-400">✓</span>
                        <span><strong className="text-white">Avoir la volonté d'apprendre</strong> - PRIsME évolue, évoluez avec</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-slate-900/50 rounded-lg p-6">
                    <h5 className="text-xl font-bold text-pink-400 mb-3">💪 Votre Opportunité</h5>
                    <p className="text-gray-300 text-lg mb-3">
                      PRIsME vous donne les <strong className="text-white">outils</strong> et la <strong className="text-white">structure</strong> 
                      pour transformer l'excellence TI. L'opportunité est là, maintenant.
                    </p>
                    <p className="text-gray-300 text-lg">
                      Avec la <strong className="text-pink-400">volonté</strong> d'apprendre, 
                      le <strong className="text-purple-400">courage</strong> d'embarquer, 
                      et la <strong className="text-indigo-400">vision</strong> de voir le potentiel, 
                      vous pouvez faire une différence significative.
                    </p>
                  </div>
                </div>
              </div>

              {/* L'Opportunité */}
              <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl p-6 border border-yellow-500/30">
                <h4 className="text-2xl font-bold text-white mb-4">Une Opportunité de Transformation</h4>
                <p className="text-gray-300 text-lg leading-relaxed mb-4">
                  Le changement peut être intimidant, c'est normal. PRIsME représente une nouvelle façon de travailler, 
                  de structurer, de penser l'excellence TI.
                </p>
                <div className="bg-slate-900/50 rounded-lg p-6 border-l-4 border-orange-500">
                  <p className="text-gray-300 text-lg">
                    C'est aussi une <strong className="text-orange-400">opportunité</strong> de transformer votre façon de servir les clients, 
                    de structurer votre savoir, et de contribuer à l'excellence TI de BZ Inc. 
                    Ensemble, nous pouvons faire une différence significative.
                  </p>
                </div>
              </div>

              {/* Le Chemin de l'Ambassadeur */}
              <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-pink-600/20 rounded-xl p-8 border-2 border-indigo-500/50">
                <h4 className="text-2xl font-bold text-white mb-6 text-center">
                  Le Chemin de l'Ambassadeur PRIsME
                </h4>

                <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/30 rounded-lg p-6 border-2 border-green-500/50 shadow-lg shadow-green-500/20">
                  <div className="text-center mb-6">
                    <div className="text-4xl mb-2">✅</div>
                    <h5 className="text-xl font-bold text-green-400">Rejoignez la Transformation</h5>
                  </div>
                  <ul className="space-y-3 text-lg text-gray-300">
                    <li className="flex items-start gap-3">
                      <span className="text-green-400 text-xl">✓</span>
                      <span><strong className="text-white">Découvrir PRIsME</strong> - Explorez la plateforme et ses possibilités</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-400 text-xl">✓</span>
                      <span><strong className="text-white">Apprendre et évoluer</strong> - PRIsME évolue, évoluez avec lui</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-400 text-xl">✓</span>
                      <span><strong className="text-white">Partager avec vos clients</strong> - Montrez-leur la puissance de PRIsME</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-green-400 text-xl">✓</span>
                      <span><strong className="text-white">Contribuer à l'excellence</strong> - Faites partie de la transformation TI de BZ Inc.</span>
                    </li>
                  </ul>
                </div>

                <div className="mt-8 text-center">
                  <p className="text-xl text-gray-300">
                    Ensemble, nous transformons l'excellence TI de BZ Inc. et servons mieux nos clients.
                  </p>
                </div>
              </div>

              {/* L'Appel Final */}
              <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-8 border border-indigo-500/30">
                <div className="text-center">
                  <h4 className="text-3xl font-bold text-white mb-4">
                    Ce n'est que le début
                  </h4>
                  <p className="text-xl text-gray-300 mb-6">
                    PRIsME est une plateforme en constante évolution. Le potentiel est <strong className="text-indigo-400">immense</strong>.
                  </p>
                  <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 mb-6">
                    Imaginez ce qu'on va accomplir ensemble.
                  </p>
                  <p className="text-lg text-gray-300">
                    Avec votre énergie, votre volonté, votre expertise et vos clients, 
                    <strong className="text-white"> PRIsME va transformer l'excellence TI de BZ Inc.</strong>
                  </p>
                </div>
              </div>

              {/* Le Dernier Message */}
              <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-xl p-6 border border-purple-500/30">
                <p className="text-center text-2xl font-bold text-white mb-4">
                  Rejoignez la transformation
                </p>
                <p className="text-center text-xl text-purple-400">
                  Devenez les ambassadeurs de PRIsME. Contribuez à l'excellence TI de BZ Inc.
                </p>
                <p className="text-center text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 mt-6">
                  PRIsME 💎
                </p>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="p-6 border-t border-indigo-500/30 bg-slate-900/50">
          <div className="flex items-center justify-end">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 font-semibold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-105"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
